// =============================================================
// PERPETUUM — design tokens (single source of truth for product code)
//
// Derived verbatim from `export/theme.ts` (Phase-5 handoff export),
// itself generated from styles/perpetuum-tokens.css + perpetuum-spec.css.
//
// Hybrid system: Iterum brand-green primary + deep-navy dark theme +
// HARD safety semantics (go/stop, status indicators) that NEVER invert
// or desaturate between light & dark. Text inks deepened to AAA where
// practical (see export/WCAG_GRID.md). Hex literals live ONLY here —
// product code consumes named tokens, never raw hex.
// =============================================================

export type ThemeName = "light" | "dark";

/** Glove-friendly metrics — absolute, theme-independent (px / numbers). */
export const metrics = {
  tapMin: 48, // absolute minimum interactive region
  tapCore: 56, // Start / Confirm / Stop
  tapHero: 72, // primary shop-floor action bar
  tapGap: 16, // ~12 mm physical clearance
  tapGapLg: 24, // clearance around destructive actions
  fieldFont: 16, // defeats iOS focus-zoom + glove use
  fieldH: 56,
  fieldHSm: 48,
  radius: { sm: 4, md: 8, lg: 16, pill: 999 },
} as const;

/** Motion — "rise into place". Durations in ms; easings are bezier control points. */
export const motion = {
  durFast: 120,
  durBase: 180,
  durMed: 260,
  durSlow: 420,
  durSlowest: 700,
  easeStandard: [0.22, 0.61, 0.36, 1] as const,
  easeEmphasized: [0.16, 1, 0.3, 1] as const,
  // honour reduced-motion: collapse durations to 0 when the OS flag is set.
} as const;

export const typography = {
  fontBody: "Manrope", // substitute until the licensed Iterum face is supplied
  fontMono: "JetBrainsMono",
  scale: { xs: 12, sm: 14, base: 16, md: 18, lg: 22, xl: 28, xxl: 36, xxxl: 48 },
  weight: {
    regular: "400",
    medium: "500",
    semibold: "600",
    bold: "700",
    black: "800",
  },
} as const;

/** Safety semantics + action fills — identical in both themes (button fills clear AAA on their ink). */
export const safety = {
  onStatus: "#FFFFFF",
  goBg: "#16BD66",
  goInk: "#06210B",
  goBgHover: "#21CC72", // Start
  stopBg: "#9E1C1C",
  stopInk: "#FFFFFF",
  stopBgHover: "#B22323", // Stop
} as const;

export type StatusKind = "critical" | "warning" | "success" | "paused" | "info";

/** Status indicator colors (dots / bars / chip glyphs) — brighten on dark, never invert. */
export const statusIndicator: Record<ThemeName, Record<StatusKind, string>> = {
  light: {
    critical: "#DC2626",
    warning: "#C2700A",
    success: "#16843E",
    paused: "#4B5563",
    info: "#1E63C9",
  },
  dark: {
    critical: "#FF4B4B",
    warning: "#F59E0B",
    success: "#10B981",
    paused: "#8E9AA6",
    info: "#3B9CFF",
  },
};

export interface Palette {
  bgMain: string;
  bgSunken: string;
  bgCard: string;
  bgRaised: string;
  bgInset: string;
  border: string;
  border2: string;
  borderStrong: string;
  hairline: string;
  fg1: string;
  fg2: string;
  fg3: string;
  fgMute: string;
  fgInv: string;
  brandPrimary: string;
  brandFill: string;
  brandFillHover: string;
  onBrand: string;
  brandWash: string;
  criticalBg: string;
  criticalInk: string;
  warningBg: string;
  warningInk: string;
  successBg: string;
  successInk: string;
  pausedBg: string;
  pausedInk: string;
  infoBg: string;
  infoInk: string;
  overlay: string;
}

/** Full per-theme palette. Text inks deepened to AAA where practical (Phase 5). */
export const palette: Record<ThemeName, Palette> = {
  light: {
    bgMain: "#F1F4F8",
    bgSunken: "#E6EBF1",
    bgCard: "#FFFFFF",
    bgRaised: "#FFFFFF",
    bgInset: "#F7F9FB",
    border: "#D2D9E2",
    border2: "#BFC8D4",
    borderStrong: "#0E1726",
    hairline: "#E7ECF2",
    fg1: "#0E1726",
    fg2: "#44505F",
    fg3: "#545E6E",
    fgMute: "#9AA4B2",
    fgInv: "#FFFFFF",
    brandPrimary: "#1A6230",
    brandFill: "#65C366",
    brandFillHover: "#4FA851",
    onBrand: "#06210B",
    brandWash: "#E8F4EA",
    criticalBg: "#FCE4E2",
    criticalInk: "#8F1010", // AAA (7.71:1)
    warningBg: "#FBEFD9",
    warningInk: "#8A4B04", // AA-large (5.97:1)
    successBg: "#E2F2E7",
    successInk: "#0D5429", // AAA (7.81:1)
    pausedBg: "#EAECEF",
    pausedInk: "#44505F",
    infoBg: "#E1ECFB",
    infoInk: "#14509E", // AAA-large / AA-normal (6.59:1)
    overlay: "rgba(14,23,38,0.46)",
  },
  dark: {
    bgMain: "#090D16",
    bgSunken: "#060911",
    bgCard: "#131B2E",
    bgRaised: "#1A2438",
    bgInset: "#0E1626",
    border: "#25304A",
    border2: "#324063",
    borderStrong: "#44567E",
    hairline: "#1B2336",
    fg1: "#F2F6FC",
    fg2: "#B4BFD2",
    fg3: "#94A1B8",
    fgMute: "#5C6781",
    fgInv: "#0E1726",
    brandPrimary: "#6FD081",
    brandFill: "#65C366",
    brandFillHover: "#79D08A",
    onBrand: "#06210B",
    brandWash: "#14271A",
    criticalBg: "#2A1115",
    criticalInk: "#FF6B6B",
    warningBg: "#2A1F0C",
    warningInk: "#F7B23C",
    successBg: "#0C2418",
    successInk: "#3FD79B",
    pausedBg: "#1B2336",
    pausedInk: "#AAB6C9",
    infoBg: "#0E2138",
    infoInk: "#6BB6FF",
    overlay: "rgba(2,4,9,0.66)",
  },
};

/** Resolve the palette for a theme. UI-less helper — no platform hook here. */
export function getPalette(theme: ThemeName): Palette {
  return palette[theme];
}

export const tokens = {
  metrics,
  motion,
  typography,
  safety,
  statusIndicator,
  palette,
} as const;

export default tokens;
